import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SponsoredInlinePreview from './SponsoredInlinePreview';
import useAdminDraftGuard from '../useAdminDraftGuard';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { adminApi } from '../../../api/admin';
import { objectStorageApi } from '../../../api/objectStorage';
import { searchApi } from '../../../api/search';
import ProgressBar from '../../../components/common/ProgressBar';
import theme from '../../../theme';

const PLACEMENTS = [
  ['HOME_DISCOVERY', '홈 · 새로운 발견'],
  ['HOME_DIVIDED_OPINION', '홈 · 평가가 갈린 앨범 아래'],
  ['EXPLORE_LATEST_KR', '탐색 · 최신 앨범(국내)'],
  ['EXPLORE_LATEST_GLOBAL', '탐색 · 최신 앨범(해외)'],
  ['EXPLORE_RECOMMENDED', '탐색 · 포스트팝 추천'],
  ['ALBUMS_RELEASES_KR', '앨범 탭 · 최신 발매(국내)'],
  ['ALBUMS_RELEASES_GLOBAL', '앨범 탭 · 최신 발매(해외)'],
  ['ALBUMS_RECOMMENDED', '앨범 탭 · 포스트팝 추천'],
  ['REVIEWS_FEED', '리뷰 탭 · 리뷰 피드'],
  ['REVIEWS_ALBUMS', '리뷰 탭 · 앨범으로 보기'],
  ['PLAYLISTS_FEED', '플리 탭 · 더 많은 플리'],
  ['SEARCH_CONTINUE', '검색 · 이어서 발견하기'],
  ['SEARCH_TASTE', '검색 · 취향으로 발견하기'],
  ['SEARCH_RESULTS', '검색 결과'],
  ['ARTIST_DETAIL', '아티스트 상세'],
  ['ALBUM_DETAIL', '앨범 상세'],
];

const PLACEMENT_ORDER = new Map(PLACEMENTS.map(([key], index) => [key, index]));
const orderedPlacementKeys = (keys) =>
  [...new Set(keys)].sort(
    (left, right) =>
      (PLACEMENT_ORDER.get(left) ?? Number.MAX_SAFE_INTEGER) -
      (PLACEMENT_ORDER.get(right) ?? Number.MAX_SAFE_INTEGER),
  );
const orderedPlacements = (placements = []) =>
  [...placements].sort(
    (left, right) =>
      (PLACEMENT_ORDER.get(left.placementKey) ?? Number.MAX_SAFE_INTEGER) -
      (PLACEMENT_ORDER.get(right.placementKey) ?? Number.MAX_SAFE_INTEGER),
  );

const EXPLORE_LATEST_PLACEMENTS = new Set(['EXPLORE_LATEST_KR', 'EXPLORE_LATEST_GLOBAL']);

const TWO_COLUMN_INLINE_PLACEMENTS = new Set([
  'ALBUMS_RELEASES_KR',
  'ALBUMS_RELEASES_GLOBAL',
  'ALBUMS_RECOMMENDED',
  'REVIEWS_ALBUMS',
]);

const FORMATS = [
  ['NATIVE_ALBUM', '네이티브 앨범'],
  ['COLLECTION', '스폰서드 컬렉션'],
  ['INLINE_BANNER', '인라인 카드'],
];

const FORMAT_LABELS = new Map(FORMATS);

const FORMAT_DESCRIPTIONS = {
  NATIVE_ALBUM: '앨범 1개를 일반 앨범 카드와 같은 모습으로 노출합니다.',
  COLLECTION: '앨범 2개 이상을 제목·설명과 함께 하나의 가로 레일로 노출합니다.',
  INLINE_BANNER: '제목·설명·이미지를 목록 사이 또는 상세 화면에 노출합니다.',
};

const PLACEMENT_RULES = {
  HOME_DISCOVERY: { formats: ['NATIVE_ALBUM'], supportsStart: true },
  HOME_DIVIDED_OPINION: { formats: ['COLLECTION'], fixedSection: true },
  EXPLORE_LATEST_KR: { formats: ['NATIVE_ALBUM'], supportsStart: true },
  EXPLORE_LATEST_GLOBAL: { formats: ['NATIVE_ALBUM'], supportsStart: true },
  EXPLORE_RECOMMENDED: { formats: ['COLLECTION'], fixedSection: true },
  ALBUMS_RELEASES_KR: {
    formats: ['NATIVE_ALBUM', 'INLINE_BANNER'],
    supportsStart: true,
    supportsRepeat: true,
  },
  ALBUMS_RELEASES_GLOBAL: {
    formats: ['NATIVE_ALBUM', 'INLINE_BANNER'],
    supportsStart: true,
    supportsRepeat: true,
  },
  ALBUMS_RECOMMENDED: {
    formats: ['NATIVE_ALBUM', 'INLINE_BANNER'],
    supportsStart: true,
    supportsRepeat: true,
  },
  REVIEWS_FEED: {
    formats: ['INLINE_BANNER'],
    supportsStart: true,
    supportsRepeat: true,
  },
  REVIEWS_ALBUMS: {
    formats: ['INLINE_BANNER'],
    supportsStart: true,
    supportsRepeat: true,
  },
  PLAYLISTS_FEED: { formats: ['INLINE_BANNER'], supportsStart: true, supportsRepeat: true },
  SEARCH_CONTINUE: { formats: ['NATIVE_ALBUM'], fixedSection: true },
  SEARCH_TASTE: { formats: ['NATIVE_ALBUM'], supportsStart: true },
  SEARCH_RESULTS: { formats: ['INLINE_BANNER'], supportsStart: true, supportsRepeat: true },
  ARTIST_DETAIL: { formats: ['INLINE_BANNER'], fixedSection: true },
  ALBUM_DETAIL: { formats: ['INLINE_BANNER'], fixedSection: true },
};

const DEFAULT_PLACEMENT_SETTINGS = {
  HOME_DISCOVERY: {
    format: 'NATIVE_ALBUM',
    startAfterOrganic: 0,
    repeatEvery: '',
    maxPerScreen: 1,
  },
  HOME_DIVIDED_OPINION: {
    format: 'COLLECTION',
    startAfterOrganic: 0,
    repeatEvery: '',
    maxPerScreen: 1,
  },
  EXPLORE_LATEST_KR: {
    format: 'NATIVE_ALBUM',
    startAfterOrganic: 0,
    repeatEvery: '',
    maxPerScreen: 1,
  },
  EXPLORE_LATEST_GLOBAL: {
    format: 'NATIVE_ALBUM',
    startAfterOrganic: 0,
    repeatEvery: '',
    maxPerScreen: 1,
  },
  EXPLORE_RECOMMENDED: {
    format: 'COLLECTION',
    startAfterOrganic: 0,
    repeatEvery: '',
    maxPerScreen: 1,
  },
  ALBUMS_RELEASES_KR: {
    format: 'NATIVE_ALBUM',
    startAfterOrganic: 10,
    repeatEvery: 10,
    maxPerScreen: 1,
  },
  ALBUMS_RELEASES_GLOBAL: {
    format: 'NATIVE_ALBUM',
    startAfterOrganic: 10,
    repeatEvery: 10,
    maxPerScreen: 1,
  },
  ALBUMS_RECOMMENDED: {
    format: 'NATIVE_ALBUM',
    startAfterOrganic: 10,
    repeatEvery: 10,
    maxPerScreen: 1,
  },
  REVIEWS_FEED: {
    format: 'INLINE_BANNER',
    startAfterOrganic: 10,
    repeatEvery: 10,
    maxPerScreen: 1,
  },
  REVIEWS_ALBUMS: {
    format: 'INLINE_BANNER',
    startAfterOrganic: 10,
    repeatEvery: 10,
    maxPerScreen: 1,
  },
  PLAYLISTS_FEED: {
    format: 'INLINE_BANNER',
    startAfterOrganic: 10,
    repeatEvery: 10,
    maxPerScreen: 1,
  },
  SEARCH_CONTINUE: {
    format: 'NATIVE_ALBUM',
    startAfterOrganic: 0,
    repeatEvery: '',
    maxPerScreen: 1,
  },
  SEARCH_TASTE: { format: 'NATIVE_ALBUM', startAfterOrganic: 0, repeatEvery: '', maxPerScreen: 1 },
  SEARCH_RESULTS: {
    format: 'INLINE_BANNER',
    startAfterOrganic: 6,
    repeatEvery: 10,
    maxPerScreen: 1,
  },
  ARTIST_DETAIL: {
    format: 'INLINE_BANNER',
    startAfterOrganic: 0,
    repeatEvery: '',
    maxPerScreen: 1,
  },
  ALBUM_DETAIL: { format: 'INLINE_BANNER', startAfterOrganic: 0, repeatEvery: '', maxPerScreen: 1 },
};

const defaultPlacementSettings = (placementKey) => ({
  ...(DEFAULT_PLACEMENT_SETTINGS[placementKey] ?? {
    format: 'NATIVE_ALBUM',
    startAfterOrganic: 0,
    repeatEvery: '',
    maxPerScreen: 1,
  }),
});

const canonicalPlacementSettings = (placementKey, value = {}, campaignFormat = '') => {
  const rule = PLACEMENT_RULES[placementKey];
  const defaults = defaultPlacementSettings(placementKey);
  const requestedFormat = campaignFormat || value.format || defaults.format;
  const format = rule?.formats.includes(requestedFormat)
    ? requestedFormat
    : (rule?.formats[0] ?? requestedFormat);
  const supportsRepeat = Boolean(rule?.supportsRepeat && format === 'INLINE_BANNER');
  const requestedStart = rule?.fixedSection
    ? 0
    : (value.startAfterOrganic ?? defaults.startAfterOrganic);
  return {
    format,
    startAfterOrganic:
      format === 'NATIVE_ALBUM' && EXPLORE_LATEST_PLACEMENTS.has(placementKey)
        ? Math.min(9, Math.max(0, Number(requestedStart) || 0))
        : requestedStart,
    repeatEvery: supportsRepeat ? (value.repeatEvery ?? defaults.repeatEvery) : '',
    maxPerScreen: supportsRepeat ? (value.maxPerScreen ?? defaults.maxPerScreen) : 1,
  };
};

const emptyForm = {
  internalName: '',
  format: '',
  advertiserName: '',
  sponsorName: '',
  sponsorNameEn: '',
  headline: '',
  headlineEn: '',
  subheadline: '',
  subheadlineEn: '',
  imageUrl: '',
  destinationType: 'ALBUM',
  destinationValue: '',
  collectionLandingUrl: '',
  whyThisAd: '현재 보고 있는 음악 콘텐츠와 관련된 광고입니다.',
  whyThisAdEn: '',
  isActive: false,
  startsAt: '',
  endsAt: '',
  selectedAlbums: [],
  placementKeys: [],
  placementSettings: {},
};

const albumIdOf = (album) => album?.albumId ?? album?.id ?? null;
const albumIdentityOf = (album) =>
  albumIdOf(album) ?? `${album?.albumSource || 'UNKNOWN'}:${album?.sourceId || ''}`;
const artistIdentityOf = (artist) =>
  artist?.artistId ?? `${artist?.artistSource || 'UNKNOWN'}:${artist?.id || ''}`;

const normalizedAlbum = (album) => ({
  ...album,
  albumId: albumIdOf(album),
  albumImageUrl: album.albumImageUrl ?? album.imageUrl ?? '',
});

const formatsOfCampaign = (campaign) => [
  ...new Set((campaign.placements ?? []).map((placement) => placement.format)),
];

const formatOfCampaign = (campaign) =>
  campaign.format ||
  (formatsOfCampaign(campaign).length === 1 ? formatsOfCampaign(campaign)[0] : '');

const availablePlacementsForFormat = (format) =>
  PLACEMENTS.filter(([placementKey]) => PLACEMENT_RULES[placementKey]?.formats.includes(format));

const startFieldPresentation = (placementKey, format, startAfterOrganic) => {
  const rawValue = Math.max(0, Number(startAfterOrganic) || 0);
  if (format === 'NATIVE_ALBUM') {
    const exploreLatest = EXPLORE_LATEST_PLACEMENTS.has(placementKey);
    return {
      label: '광고 앨범이 등장할 순서',
      helperText: exploreLatest
        ? '1~10 중 선택 · 이 탐색 홈 위치에만 적용'
        : '1이면 첫 번째, 4이면 네 번째 앨범 카드로 표시',
      min: 1,
      max: exploreLatest ? 10 : undefined,
      value: rawValue + 1,
      toStoredValue: (value) =>
        Math.min(
          exploreLatest ? 9 : Number.MAX_SAFE_INTEGER,
          Math.max(0, (Number(value) || 1) - 1),
        ),
    };
  }
  if (format === 'INLINE_BANNER' && TWO_COLUMN_INLINE_PLACEMENTS.has(placementKey)) {
    return {
      label: '인라인 카드 앞의 앨범 행 수',
      helperText: '앨범은 한 행에 2개입니다. 3이면 앨범 6개(3행) 다음에 표시',
      min: 0,
      value: Math.ceil(rawValue / 2),
      toStoredValue: (value) => Math.max(0, Number(value) || 0) * 2,
    };
  }
  return {
    label: '인라인 카드 앞의 일반 콘텐츠 수',
    helperText: '예: 10이면 일반 콘텐츠 10개 다음에 표시',
    min: 0,
    value: rawValue,
    toStoredValue: (value) => Math.max(0, Number(value) || 0),
  };
};

const repeatFieldPresentation = (placementKey, repeatEvery) => {
  if (TWO_COLUMN_INLINE_PLACEMENTS.has(placementKey)) {
    return {
      label: '다음 인라인 카드 사이의 앨범 행 수',
      helperText: '비우면 한 번만 표시 · 반복 시 최소 3행(앨범 6개)',
      min: 3,
      value: repeatEvery === '' || repeatEvery == null ? '' : Math.ceil(Number(repeatEvery) / 2),
      toStoredValue: (value) => (value === '' ? '' : Math.max(3, Number(value) || 3) * 2),
    };
  }
  return {
    label: '다음 광고 사이의 일반 콘텐츠 수',
    helperText: '비우면 한 번만 표시 · 반복 시 최소 6',
    min: 6,
    value: repeatEvery,
    toStoredValue: (value) => value,
  };
};

const placementSlotDescription = (placementKey, format, slot) => {
  if (format === 'NATIVE_ALBUM') return `${slot + 1}번째 앨범 카드`;
  if (format === 'INLINE_BANNER' && TWO_COLUMN_INLINE_PLACEMENTS.has(placementKey)) {
    return slot === 0 ? '앨범 목록 맨 앞' : `앨범 ${Math.ceil(slot / 2)}행 뒤`;
  }
  return slot === 0 ? '목록 맨 앞' : `일반 콘텐츠 ${slot}개 뒤`;
};

const campaignStatus = (campaign) => {
  if (!campaign.isActive) return { label: '초안/중지', color: 'default' };
  const now = Date.now();
  const startsAt = campaign.startsAt ? Date.parse(campaign.startsAt) : null;
  const endsAt = campaign.endsAt ? Date.parse(campaign.endsAt) : null;
  if (startsAt != null && startsAt > now) return { label: '예약', color: 'info' };
  if (endsAt != null && endsAt <= now) return { label: '종료', color: 'default' };
  return { label: '노출 중', color: 'success' };
};

const intervalsOverlap = (leftStart, leftEnd, rightStart, rightEnd) =>
  (leftEnd == null || rightStart == null || rightStart < leftEnd) &&
  (rightEnd == null || leftStart == null || leftStart < rightEnd);

const placementSlots = (settings, placementKey, format) => {
  const slots = [];
  let slot = Math.max(0, Number(settings.startAfterOrganic) || 0);
  const max = Math.min(Math.max(Number(settings.maxPerScreen) || 1, 1), 3);
  for (let occurrence = 0; occurrence < max; occurrence += 1) {
    slots.push(
      format === 'INLINE_BANNER' && TWO_COLUMN_INLINE_PLACEMENTS.has(placementKey)
        ? Math.ceil(slot / 2) * 2
        : slot,
    );
    if (settings.repeatEvery === '' || settings.repeatEvery == null) break;
    slot += Math.max(Number(settings.repeatEvery) || 0, 6);
  }
  return slots;
};

const generatedInternalName = (form) => {
  const now = new Date();
  const date = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('');
  const time = `${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
  const materialName =
    form.format === 'NATIVE_ALBUM' ? form.selectedAlbums[0]?.title?.trim() : form.headline.trim();
  const readableName = [form.advertiserName.trim() || form.sponsorName.trim(), materialName]
    .filter(Boolean)
    .join(' · ');
  return `${readableName || '광고 캠페인'} · ${date}-${time}`.slice(0, 120);
};

const DATE_TIME_INPUT_TEMPLATE = 'YYYY. MM. DD. HH:MM';
const DATE_TIME_INPUT_FONT = 'SFMono-Regular, Consolas, "Liberation Mono", monospace';

const maskDateTimeInput = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 12);
  let masked = digits.slice(0, 4);
  if (digits.length >= 4) masked += '. ';
  masked += digits.slice(4, 6);
  if (digits.length >= 6) masked += '. ';
  masked += digits.slice(6, 8);
  if (digits.length >= 8) masked += '. ';
  masked += digits.slice(8, 10);
  if (digits.length >= 10) masked += ':';
  masked += digits.slice(10, 12);
  return masked;
};

const toDateTimeInput = (value) => {
  const match = value?.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  return match ? `${match[1]}. ${match[2]}. ${match[3]}. ${match[4]}:${match[5]}` : '';
};

const toPayloadDateTime = (value) => {
  if (!value) return null;
  const match = value.match(/^(\d{4})\. (\d{2})\. (\d{2})\. (\d{2}):(\d{2})$/);
  if (!match) return undefined;

  const [, yearText, monthText, dayText, hourText, minuteText] = match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const hour = Number(hourText);
  const minute = Number(minuteText);
  const lastDay = month >= 1 && month <= 12 ? new Date(year, month, 0).getDate() : 0;
  if (
    year < 1000 ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > lastDay ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59
  ) {
    return undefined;
  }

  return `${yearText}-${monthText}-${dayText}T${hourText}:${minuteText}`;
};

const MaskedDateTimeField = ({ label, value, onChange }) => (
  <Box sx={{ position: 'relative', flex: 1 }}>
    <TextField
      fullWidth
      type="text"
      label={label}
      InputLabelProps={{ shrink: true }}
      inputProps={{ inputMode: 'numeric', maxLength: DATE_TIME_INPUT_TEMPLATE.length }}
      value={value}
      onChange={onChange}
      sx={{
        '& .MuiInputBase-input': {
          fontFamily: DATE_TIME_INPUT_FONT,
          letterSpacing: 'normal',
        },
      }}
    />
    {value.length < DATE_TIME_INPUT_TEMPLATE.length && (
      <Typography
        component="span"
        aria-hidden="true"
        color="text.disabled"
        sx={{
          position: 'absolute',
          zIndex: 2,
          top: '50%',
          left: 14,
          transform: 'translateY(-50%)',
          pointerEvents: 'none',
          whiteSpace: 'pre',
          fontFamily: DATE_TIME_INPUT_FONT,
          fontSize: '1rem',
          lineHeight: '1.4375em',
          letterSpacing: 'normal',
        }}
      >
        {' '.repeat(value.length)}
        {DATE_TIME_INPUT_TEMPLATE.slice(value.length)}
      </Typography>
    )}
  </Box>
);

const toForm = (campaign) => {
  const placements = orderedPlacements(campaign.placements ?? []);
  const format = formatOfCampaign(campaign);
  const placementSettings = Object.fromEntries(
    placements.map((item) => [
      item.placementKey,
      {
        ...canonicalPlacementSettings(item.placementKey, item, format),
      },
    ]),
  );
  const placementKeys = orderedPlacementKeys(placements.map((item) => item.placementKey));
  return {
    internalName: campaign.internalName ?? '',
    format,
    advertiserName: campaign.advertiserName ?? '',
    sponsorName: campaign.sponsorName ?? '',
    sponsorNameEn: campaign.sponsorNameEn ?? '',
    headline: campaign.headline ?? '',
    headlineEn: campaign.headlineEn ?? '',
    subheadline: campaign.subheadline ?? '',
    subheadlineEn: campaign.subheadlineEn ?? '',
    imageUrl: campaign.imageUrl ?? '',
    destinationType: campaign.destinationType ?? 'ALBUM',
    destinationValue: campaign.destinationValue ?? '',
    collectionLandingUrl: campaign.collectionLandingUrl ?? '',
    whyThisAd: campaign.whyThisAd ?? emptyForm.whyThisAd,
    whyThisAdEn: campaign.whyThisAdEn ?? '',
    isActive: Boolean(campaign.isActive),
    startsAt: toDateTimeInput(campaign.startsAt),
    endsAt: toDateTimeInput(campaign.endsAt),
    selectedAlbums: (campaign.albums ?? []).map(normalizedAlbum),
    placementKeys,
    placementSettings,
  };
};

const payloadFrom = (form, fallbackInternalName) => ({
  internalName: form.internalName.trim() || fallbackInternalName,
  format: form.format || null,
  advertiserName: form.advertiserName.trim() || null,
  sponsorName: form.sponsorName.trim(),
  sponsorNameEn: form.sponsorNameEn.trim() || null,
  headline:
    form.format === 'NATIVE_ALBUM'
      ? form.selectedAlbums[0]?.title?.trim() || form.headline.trim()
      : form.headline.trim(),
  headlineEn: form.format === 'NATIVE_ALBUM' ? null : form.headlineEn.trim() || null,
  subheadline: form.format === 'NATIVE_ALBUM' ? null : form.subheadline.trim() || null,
  subheadlineEn: form.format === 'NATIVE_ALBUM' ? null : form.subheadlineEn.trim() || null,
  imageUrl: form.format === 'INLINE_BANNER' ? form.imageUrl.trim() || null : null,
  destinationType: form.format === 'INLINE_BANNER' ? form.destinationType : 'ALBUM',
  destinationValue: form.format === 'INLINE_BANNER' ? form.destinationValue.trim() || null : null,
  collectionLandingUrl:
    form.format === 'COLLECTION' ? form.collectionLandingUrl.trim() || null : null,
  whyThisAd: form.whyThisAd.trim(),
  whyThisAdEn: form.whyThisAdEn.trim() || null,
  isActive: form.isActive,
  startsAt: toPayloadDateTime(form.startsAt),
  endsAt: toPayloadDateTime(form.endsAt),
  albumIds: (form.format === 'INLINE_BANNER' && form.destinationType !== 'ALBUM'
    ? []
    : form.selectedAlbums
  )
    .map(albumIdOf)
    .filter((value) => Number.isInteger(value) && value > 0),
  placements: orderedPlacementKeys(form.placementKeys).map((placementKey) => {
    const settings = canonicalPlacementSettings(
      placementKey,
      form.placementSettings[placementKey],
      form.format,
    );
    return {
      placementKey,
      format: settings.format,
      position: 0,
      startAfterOrganic: Number(settings.startAfterOrganic) || 0,
      repeatEvery: settings.repeatEvery === '' ? null : Number(settings.repeatEvery),
      maxPerScreen: Number(settings.maxPerScreen) || 1,
    };
  }),
});

export default function SponsoredCampaignManagement() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(undefined);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [albumSearchText, setAlbumSearchText] = useState('');
  const [albumOptions, setAlbumOptions] = useState([]);
  const [albumSearching, setAlbumSearching] = useState(false);
  const [albumResolving, setAlbumResolving] = useState(false);
  const [targetSearchText, setTargetSearchText] = useState('');
  const [targetOptions, setTargetOptions] = useState([]);
  const [targetSearching, setTargetSearching] = useState(false);
  const [targetResolving, setTargetResolving] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [expandedCampaignIds, setExpandedCampaignIds] = useState([]);
  const [deletingCampaign, setDeletingCampaign] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [placementInputDrafts, setPlacementInputDrafts] = useState({});
  const [togglingId, setTogglingId] = useState(null);
  const togglingRef = useRef(false);
  const draft = useAdminDraftGuard(
    { form, imageFile, placementInputDrafts },
    { enabled: editing !== undefined, busy: saving },
  );

  const toggleActive = async (campaign) => {
    if (togglingRef.current) return;
    togglingRef.current = true;
    setTogglingId(campaign.id);
    setError('');
    try {
      const response = await adminApi.setSponsoredCampaignActive(
        campaign.id,
        !campaign.isActive,
        campaign.isActive,
      );
      setCampaigns((current) =>
        current.map((item) => (item.id === campaign.id ? response.data : item)),
      );
    } catch (requestError) {
      setError(
        requestError.message ||
          '캠페인 활성 상태를 변경하지 못했습니다. 목록을 새로고침한 뒤 다시 시도해주세요.',
      );
    } finally {
      togglingRef.current = false;
      setTogglingId(null);
    }
  };

  const placementNames = useMemo(() => new Map(PLACEMENTS), []);
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await adminApi.getSponsoredCampaigns();
      setCampaigns(response.data ?? []);
    } catch (requestError) {
      setError(requestError.message || '광고 캠페인을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (editing === undefined || albumSearchText.trim().length < 1) {
      setAlbumOptions([]);
      setAlbumSearching(false);
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setAlbumSearching(true);
      try {
        const response = await adminApi.searchSponsoredAlbums(albumSearchText.trim());
        if (!cancelled) {
          setAlbumOptions((response.data ?? []).map(normalizedAlbum));
        }
      } catch (requestError) {
        if (!cancelled) setFormError(requestError.message || '앨범을 검색하지 못했습니다.');
      } finally {
        if (!cancelled) setAlbumSearching(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [albumSearchText, editing]);

  useEffect(() => {
    if (
      editing === undefined ||
      !['ARTIST', 'PLAYLIST'].includes(form.destinationType) ||
      targetSearchText.trim().length < (form.destinationType === 'ARTIST' ? 1 : 2)
    ) {
      setTargetOptions([]);
      setTargetSearching(false);
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setTargetSearching(true);
      try {
        const response =
          form.destinationType === 'ARTIST'
            ? await adminApi.searchSponsoredArtists(targetSearchText.trim())
            : await searchApi.searchCategory('lists', targetSearchText.trim());
        if (!cancelled) {
          setTargetOptions(
            (response.data ?? []).filter(
              (item) => item.slug && (form.destinationType !== 'ARTIST' || item.id),
            ),
          );
        }
      } catch (requestError) {
        if (!cancelled) setFormError(requestError.message || '이동 대상을 검색하지 못했습니다.');
      } finally {
        if (!cancelled) setTargetSearching(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [editing, form.destinationType, targetSearchText]);

  useEffect(
    () => () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    },
    [imagePreview],
  );

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, placementKeys: [], placementSettings: {} });
    setFormError('');
    setImageFile(null);
    setImagePreview('');
    setAlbumSearchText('');
    setAlbumOptions([]);
    setTargetSearchText('');
    setTargetOptions([]);
    setSelectedTarget(null);
    setPlacementInputDrafts({});
  };

  const close = () => {
    if (draft.confirmDiscard()) {
      setEditing(undefined);
      setFormError('');
      setImageFile(null);
      setImagePreview('');
      setPlacementInputDrafts({});
    }
  };

  const save = async () => {
    const payload = payloadFrom(form, generatedInternalName(form));
    if (
      (form.startsAt && payload.startsAt === undefined) ||
      (form.endsAt && payload.endsAt === undefined)
    ) {
      setFormError('시작과 종료는 연도 4자리의 유효한 날짜·시간으로 입력해주세요.');
      return;
    }
    if (!payload.advertiserName) {
      setFormError('관리용 광고주명을 입력해주세요.');
      return;
    }
    if (!payload.format) {
      setFormError('광고 형식을 먼저 선택해주세요.');
      return;
    }
    if (!payload.headline || payload.placements.length === 0) {
      setFormError('광고 소재와 노출 위치를 확인해주세요.');
      return;
    }
    if (payload.format === 'NATIVE_ALBUM' && payload.albumIds.length !== 1) {
      setFormError('네이티브 앨범에 표시할 앨범을 정확히 하나 선택해주세요.');
      return;
    }
    if (payload.format === 'COLLECTION' && payload.albumIds.length < 2) {
      setFormError('스폰서드 컬렉션에는 서로 다른 앨범을 두 개 이상 선택해주세요.');
      return;
    }
    if (
      payload.format === 'INLINE_BANNER' &&
      payload.destinationType === 'ALBUM' &&
      payload.albumIds.length !== 1
    ) {
      setFormError('인라인 카드를 눌렀을 때 열 앨범을 정확히 하나 선택해주세요.');
      return;
    }
    if (
      payload.format === 'INLINE_BANNER' &&
      ['ARTIST', 'PLAYLIST', 'EXTERNAL'].includes(payload.destinationType) &&
      !payload.destinationValue
    ) {
      setFormError('인라인 카드를 눌렀을 때 이동할 대상을 선택하거나 주소를 입력해주세요.');
      return;
    }
    if (form.isActive && activePlacementConflicts.length > 0) {
      setFormError(
        '같은 기간·노출 위치에서 동일한 형식의 광고 슬롯이 겹칩니다. 위치별 노출 방식의 상세 안내를 확인해주세요.',
      );
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      if (imageFile) {
        const imageFormData = new FormData();
        imageFormData.append('image', imageFile);
        const uploadResponse = await objectStorageApi.uploadImage(imageFormData);
        const uploadedUrl = uploadResponse.data?.url;
        if (!uploadedUrl) throw new Error('이미지 업로드 결과를 확인할 수 없습니다.');
        payload.imageUrl = uploadedUrl;
      }
      if (editing?.id) await adminApi.updateSponsoredCampaign(editing.id, payload);
      else await adminApi.createSponsoredCampaign(payload);
      setEditing(undefined);
      setImageFile(null);
      setImagePreview('');
      await load();
    } catch (requestError) {
      setFormError(requestError.message || '캠페인을 저장하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const openDelete = (campaign) => {
    setDeletingCampaign(campaign);
    setDeleteError('');
  };

  const closeDelete = () => {
    if (deleting) return;
    setDeletingCampaign(null);
    setDeleteError('');
  };

  const confirmDelete = async () => {
    if (!deletingCampaign?.id) return;
    setDeleting(true);
    setDeleteError('');
    try {
      await adminApi.deleteSponsoredCampaign(deletingCampaign.id);
      setDeletingCampaign(null);
      await load();
    } catch (requestError) {
      setDeleteError(requestError.message || '캠페인을 삭제하지 못했습니다.');
    } finally {
      setDeleting(false);
    }
  };

  const field = (key) => ({
    value: form[key],
    onChange: (event) => setForm((current) => ({ ...current, [key]: event.target.value })),
  });

  const dateTimeField = (key) => ({
    value: form[key],
    onChange: (event) =>
      setForm((current) => ({ ...current, [key]: maskDateTimeInput(event.target.value) })),
  });

  const clearField = (key) => {
    setForm((current) => ({ ...current, [key]: '' }));
  };

  const selectImage = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      setFormError('PNG, JPG 또는 WebP 이미지를 선택해주세요.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setFormError('이미지는 5MB 이하로 선택해주세요.');
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setFormError('');
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview('');
    setForm((current) => ({ ...current, imageUrl: '' }));
  };

  const changeDestinationType = (destinationType) => {
    setSelectedTarget(null);
    setTargetSearchText('');
    setTargetOptions([]);
    setForm((current) => ({
      ...current,
      destinationType,
      destinationValue: '',
      selectedAlbums: destinationType === 'ALBUM' ? current.selectedAlbums.slice(0, 1) : [],
    }));
  };

  const selectAlbums = async (_, value, reason, details) => {
    if (reason === 'removeOption' || reason === 'clear') {
      setForm((current) => ({
        ...current,
        selectedAlbums: current.format === 'COLLECTION' ? value : value.slice(-1),
      }));
      return;
    }
    const candidate = details?.option;
    if (!candidate) return;
    let resolved = normalizedAlbum(candidate);
    if (!Number.isInteger(resolved.albumId) || resolved.albumId <= 0) {
      setAlbumResolving(true);
      try {
        const response = await adminApi.resolveSponsoredAlbum(resolved);
        resolved = normalizedAlbum(response.data);
      } catch (requestError) {
        setFormError(requestError.message || '선택한 앨범을 등록하지 못했습니다.');
        return;
      } finally {
        setAlbumResolving(false);
      }
    }
    const candidateIdentity = albumIdentityOf(candidate);
    const resolvedValue = value.map((album) =>
      albumIdentityOf(album) === candidateIdentity ? resolved : normalizedAlbum(album),
    );
    setForm((current) => ({
      ...current,
      selectedAlbums: current.format === 'COLLECTION' ? resolvedValue : resolvedValue.slice(-1),
    }));
    setFormError('');
    setAlbumSearchText('');
  };

  const selectTarget = async (_, value) => {
    if (!value) {
      setSelectedTarget(null);
      setForm((current) => ({ ...current, destinationValue: '' }));
      return;
    }
    let resolved = value;
    if (form.destinationType === 'ARTIST' && !Number.isInteger(value.artistId)) {
      setTargetResolving(true);
      try {
        const response = await adminApi.resolveSponsoredArtist(value);
        resolved = response.data;
      } catch (requestError) {
        setFormError(requestError.message || '선택한 아티스트를 등록하지 못했습니다.');
        return;
      } finally {
        setTargetResolving(false);
      }
    }
    setSelectedTarget(resolved);
    setForm((current) => ({
      ...current,
      destinationValue: resolved.slug || '',
    }));
    setFormError('');
  };

  const changeCampaignFormat = (format) => {
    setSelectedTarget(null);
    setTargetSearchText('');
    setTargetOptions([]);
    setPlacementInputDrafts({});
    if (format !== 'INLINE_BANNER') {
      setImageFile(null);
      setImagePreview('');
    }
    setForm((current) => {
      const placementKeys = orderedPlacementKeys(
        current.placementKeys.filter((placementKey) =>
          PLACEMENT_RULES[placementKey]?.formats.includes(format),
        ),
      );
      const placementSettings = Object.fromEntries(
        placementKeys.map((placementKey) => [
          placementKey,
          canonicalPlacementSettings(placementKey, current.placementSettings[placementKey], format),
        ]),
      );
      const selectedAlbums =
        format === 'COLLECTION' ? current.selectedAlbums : current.selectedAlbums.slice(0, 1);
      return {
        ...current,
        format,
        imageUrl: format === 'INLINE_BANNER' ? current.imageUrl : '',
        destinationType: format === 'INLINE_BANNER' ? current.destinationType : 'ALBUM',
        destinationValue: format === 'INLINE_BANNER' ? current.destinationValue : '',
        selectedAlbums:
          format === 'INLINE_BANNER' && current.destinationType !== 'ALBUM' ? [] : selectedAlbums,
        placementKeys,
        placementSettings,
      };
    });
    setFormError('');
  };

  const changePlacements = (nextPlacementKeys) => {
    setPlacementInputDrafts({});
    setForm((current) => {
      const requestedKeys = orderedPlacementKeys(nextPlacementKeys);
      const placementSettings = { ...current.placementSettings };
      requestedKeys.forEach((placementKey) => {
        if (!placementSettings[placementKey]) {
          placementSettings[placementKey] = canonicalPlacementSettings(
            placementKey,
            defaultPlacementSettings(placementKey),
            current.format,
          );
        }
      });
      return { ...current, placementKeys: requestedKeys, placementSettings };
    });
  };

  const changePlacementSetting = (placementKey, key, value) => {
    setForm((current) => {
      const nextSettings = {
        ...current.placementSettings,
        [placementKey]: canonicalPlacementSettings(
          placementKey,
          {
            ...(current.placementSettings[placementKey] ?? defaultPlacementSettings(placementKey)),
            [key]: value,
            ...(key === 'repeatEvery' && value === '' ? { maxPerScreen: 1 } : {}),
          },
          current.format,
        ),
      };
      return { ...current, placementSettings: nextSettings };
    });
  };

  const placementDraftKey = (placementKey, key) => `${placementKey}:${key}`;

  const placementInputValue = (placementKey, key, fallbackValue) => {
    const draftKey = placementDraftKey(placementKey, key);
    return Object.prototype.hasOwnProperty.call(placementInputDrafts, draftKey)
      ? placementInputDrafts[draftKey]
      : fallbackValue;
  };

  const changePlacementNumberInput = (
    placementKey,
    key,
    rawValue,
    toStoredValue,
    allowEmpty = false,
  ) => {
    const draftKey = placementDraftKey(placementKey, key);
    setPlacementInputDrafts((current) => ({ ...current, [draftKey]: rawValue }));
    if (rawValue === '' && !allowEmpty) return;
    changePlacementSetting(placementKey, key, toStoredValue(rawValue));
  };

  const finishPlacementNumberInput = (placementKey, key) => {
    const draftKey = placementDraftKey(placementKey, key);
    setPlacementInputDrafts((current) => {
      if (!Object.prototype.hasOwnProperty.call(current, draftKey)) return current;
      const next = { ...current };
      delete next[draftKey];
      return next;
    });
  };

  const moveSelectedAlbum = (index, direction) => {
    setForm((current) => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= current.selectedAlbums.length) return current;
      const selectedAlbums = [...current.selectedAlbums];
      [selectedAlbums[index], selectedAlbums[nextIndex]] = [
        selectedAlbums[nextIndex],
        selectedAlbums[index],
      ];
      return { ...current, selectedAlbums };
    });
  };

  const removeSelectedAlbum = (index) => {
    setForm((current) => ({
      ...current,
      selectedAlbums: current.selectedAlbums.filter((_, albumIndex) => albumIndex !== index),
    }));
  };

  const hasCollectionPlacement = form.format === 'COLLECTION';
  const selectablePlacements = availablePlacementsForFormat(form.format);
  const activePlacementConflicts = useMemo(() => {
    if (!form.isActive || !form.placementKeys.length) return [];
    const start = toPayloadDateTime(form.startsAt);
    const end = toPayloadDateTime(form.endsAt);
    if (start === undefined || end === undefined) return [];
    const requestedStart = start ? Date.parse(start) : null;
    const requestedEnd = end ? Date.parse(end) : null;
    return campaigns.flatMap((campaign) => {
      if (campaign.id === editing?.id || !campaign.isActive) return [];
      if (
        !intervalsOverlap(
          requestedStart,
          requestedEnd,
          campaign.startsAt ? Date.parse(campaign.startsAt) : null,
          campaign.endsAt ? Date.parse(campaign.endsAt) : null,
        )
      )
        return [];
      return campaign.placements.flatMap((placement) => {
        if (
          placement.format !== form.format ||
          !form.placementKeys.includes(placement.placementKey) ||
          PLACEMENT_RULES[placement.placementKey]?.fixedSection
        )
          return [];
        const requestedSettings = canonicalPlacementSettings(
          placement.placementKey,
          form.placementSettings[placement.placementKey],
          form.format,
        );
        const sharedSlots = placementSlots(
          requestedSettings,
          placement.placementKey,
          form.format,
        ).filter((slot) =>
          placementSlots(placement, placement.placementKey, placement.format).includes(slot),
        );
        return sharedSlots.length
          ? [{ campaign, placementKey: placement.placementKey, slots: sharedSlots }]
          : [];
      });
    });
  }, [
    campaigns,
    editing?.id,
    form.endsAt,
    form.format,
    form.isActive,
    form.placementKeys,
    form.placementSettings,
    form.startsAt,
  ]);

  return (
    <ProgressBar isLoading={loading}>
      <Stack spacing={2} sx={{ maxWidth: 1180 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            광고 캠페인
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            앱 업데이트 없이 위치·기간·형식을 제어합니다. 활성 캠페인이 없으면 기존 콘텐츠만
            표시됩니다.
          </Typography>
        </Box>
        {error ? (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        ) : null}
        {campaigns.some((campaign) => formatsOfCampaign(campaign).length > 1) ? (
          <Alert severity="warning">
            서로 다른 광고 형식이 섞였던 과거 캠페인은 전달·클릭 기록을 보존하기 위해 읽기 전용으로
            표시합니다. 새 캠페인과 편집 가능한 캠페인은 모두 단일 형식입니다.
          </Alert>
        ) : null}
        <Box>
          <Button variant="contained" onClick={openCreate}>
            캠페인 만들기
          </Button>
        </Box>
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: theme.palette.grey.bg }}>
                <TableCell>상태</TableCell>
                <TableCell>캠페인</TableCell>
                <TableCell>형식</TableCell>
                <TableCell>관리 광고주</TableCell>
                <TableCell>배치별 전달 / 클릭 · CTR</TableCell>
                <TableCell>기간</TableCell>
                <TableCell align="right">전체 전달 / 클릭 · CTR</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {campaigns.map((campaign) => (
                <TableRow key={campaign.id} hover>
                  <TableCell>
                    <Stack alignItems="flex-start" spacing={0.5}>
                      {(() => {
                        const status = campaignStatus(campaign);
                        return <Chip size="small" color={status.color} label={status.label} />;
                      })()}
                      <FormControlLabel
                        sx={{ ml: -1, mr: 0, whiteSpace: 'nowrap' }}
                        control={
                          <Switch
                            size="small"
                            checked={campaign.isActive}
                            disabled={
                              togglingId !== null ||
                              (!campaign.isActive && formatsOfCampaign(campaign).length > 1)
                            }
                            onChange={() => toggleActive(campaign)}
                            inputProps={{ 'aria-label': `${campaign.internalName} 캠페인 활성화` }}
                          />
                        }
                        label={
                          <Typography variant="caption">
                            {togglingId === campaign.id
                              ? '변경 중…'
                              : campaign.isActive
                                ? '활성'
                                : '비활성'}
                          </Typography>
                        }
                      />
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={600}>{campaign.internalName}</Typography>
                    <Typography variant="caption">{campaign.headline}</Typography>
                  </TableCell>
                  <TableCell>
                    {formatsOfCampaign(campaign).length > 1 ? (
                      <Chip color="warning" label="형식 혼합됨" size="small" />
                    ) : (
                      FORMAT_LABELS.get(formatOfCampaign(campaign)) || '확인 필요'
                    )}
                  </TableCell>
                  <TableCell>
                    {campaign.advertiserName || campaign.sponsorName || '미등록'}
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.5}>
                      {(expandedCampaignIds.includes(campaign.id)
                        ? orderedPlacements(campaign.placements)
                        : orderedPlacements(campaign.placements).slice(0, 3)
                      ).map((item) => {
                        const stats = campaign.placementStats?.find(
                          (value) => value.placementKey === item.placementKey,
                        );
                        return (
                          <Box key={item.placementKey}>
                            <Typography variant="body2">
                              {placementNames.get(item.placementKey) || item.placementKey}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {(stats?.deliveryCount ?? 0).toLocaleString()} /{' '}
                              {(stats?.clickCount ?? 0).toLocaleString()} ·{' '}
                              {((stats?.clickThroughRate ?? 0) * 100).toFixed(1)}%
                            </Typography>
                          </Box>
                        );
                      })}
                      {campaign.placements.length > 3 ? (
                        <Button
                          size="small"
                          onClick={() =>
                            setExpandedCampaignIds((current) =>
                              current.includes(campaign.id)
                                ? current.filter((id) => id !== campaign.id)
                                : [...current, campaign.id],
                            )
                          }
                          sx={{ alignSelf: 'flex-start', px: 0 }}
                        >
                          {expandedCampaignIds.includes(campaign.id)
                            ? '접기'
                            : `${campaign.placements.length - 3}개 위치 더보기`}
                        </Button>
                      ) : null}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {campaign.startsAt?.slice(0, 16) || '즉시'} ~{' '}
                    {campaign.endsAt?.slice(0, 16) || '계속'}
                  </TableCell>
                  <TableCell align="right">
                    {campaign.impressionCount.toLocaleString()} /{' '}
                    {campaign.clickCount.toLocaleString()} ·{' '}
                    {campaign.impressionCount > 0
                      ? `${((campaign.clickCount / campaign.impressionCount) * 100).toFixed(1)}%`
                      : '0.0%'}
                  </TableCell>
                  <TableCell>
                    <Stack direction="column" spacing={0.5}>
                      <Button
                        disabled={togglingId !== null || formatsOfCampaign(campaign).length > 1}
                        onClick={() => {
                          setEditing(campaign);
                          setForm(toForm(campaign));
                          setFormError('');
                          setImageFile(null);
                          setImagePreview('');
                          setAlbumSearchText('');
                          setAlbumOptions([]);
                          setTargetSearchText('');
                          setTargetOptions([]);
                          setSelectedTarget(null);
                          setPlacementInputDrafts({});
                        }}
                      >
                        {formatsOfCampaign(campaign).length > 1 ? '과거 기록' : '편집'}
                      </Button>
                      <Button
                        color="error"
                        disabled={togglingId !== null}
                        onClick={() => openDelete(campaign)}
                      >
                        삭제
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {campaigns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    등록된 캠페인이 없습니다.
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>

      <Dialog open={editing !== undefined} onClose={close} maxWidth="md" fullWidth>
        <DialogTitle>{editing?.id ? '캠페인 편집' : '캠페인 만들기'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">
              내부 관리용 이름과 연결 ID는 자동으로 처리합니다. 앱에 보일 내용과 이동할 대상을
              선택해주세요.
            </Alert>
            {formError ? (
              <Alert
                severity="error"
                onClose={() => setFormError('')}
                sx={{ position: 'sticky', top: 0, zIndex: 3 }}
              >
                {formError}
              </Alert>
            ) : null}

            <Typography variant="h6" fontWeight={700}>
              1. 관리 정보
            </Typography>
            <TextField
              required
              fullWidth
              label="관리용 광고주명"
              helperText="앱에는 표시되지 않습니다. 캠페인 목록과 CTR 확인에서 광고주를 구분할 때 사용합니다."
              {...field('advertiserName')}
            />
            <TextField
              fullWidth
              label="앱에 표시할 광고주명 (선택)"
              helperText="입력하면 ‘광고 · 광고주명’, 비워두면 ‘광고’만 표시됩니다."
              {...field('sponsorName')}
            />

            <Typography variant="h6" fontWeight={700} sx={{ pt: 1 }}>
              2. 광고 형식
            </Typography>
            <FormControl required>
              <RadioGroup
                value={form.format}
                onChange={(event) => changeCampaignFormat(event.target.value)}
              >
                <Stack spacing={1}>
                  {FORMATS.map(([value, label]) => (
                    <Paper
                      key={value}
                      variant="outlined"
                      sx={{
                        borderColor: form.format === value ? theme.palette.primary.main : undefined,
                        px: 1.5,
                        py: 0.5,
                      }}
                    >
                      <FormControlLabel
                        value={value}
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography fontWeight={600}>{label}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {FORMAT_DESCRIPTIONS[value]}
                            </Typography>
                          </Box>
                        }
                        sx={{ m: 0, width: '100%', alignItems: 'flex-start' }}
                      />
                    </Paper>
                  ))}
                </Stack>
              </RadioGroup>
            </FormControl>

            <Typography variant="h6" fontWeight={700} sx={{ pt: 1 }}>
              3. 광고 소재와 이동
            </Typography>
            {!form.format ? (
              <Alert severity="info">광고 형식을 선택하면 필요한 입력 항목만 표시됩니다.</Alert>
            ) : null}
            {form.format && form.format !== 'NATIVE_ALBUM' ? (
              <>
                <TextField
                  required
                  fullWidth
                  label={form.format === 'COLLECTION' ? '컬렉션 제목' : '광고 제목'}
                  inputProps={{ maxLength: 160 }}
                  helperText={`${form.headline.length}/160자`}
                  {...field('headline')}
                />
                <TextField
                  fullWidth
                  label={form.format === 'COLLECTION' ? '컬렉션 설명 (선택)' : '광고 설명 (선택)'}
                  inputProps={{ maxLength: 240 }}
                  helperText={`제목 아래에 표시할 짧은 문장입니다. ${form.subheadline.length}/240자`}
                  {...field('subheadline')}
                />
              </>
            ) : null}

            {form.format === 'INLINE_BANNER' ? (
              <Stack spacing={1}>
                <Typography fontWeight={600}>광고 이미지 (선택)</Typography>
                <Typography variant="body2" color="text.secondary">
                  PNG, JPG 또는 WebP 파일을 선택하면 저장할 때 포스트팝 저장소에 업로드합니다.
                  앱에서는 정사각형으로 잘릴 수 있으므로 중요한 내용은 이미지 중앙에 배치해주세요.
                </Typography>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ sm: 'center' }}
                >
                  <Button component="label" variant="outlined" sx={{ alignSelf: 'flex-start' }}>
                    이미지 선택
                    <input
                      hidden
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={selectImage}
                    />
                  </Button>
                  {imagePreview || form.imageUrl ? (
                    <Button color="inherit" onClick={clearImage} sx={{ alignSelf: 'flex-start' }}>
                      이미지 제거
                    </Button>
                  ) : null}
                </Stack>
                {imagePreview || form.imageUrl ? (
                  <Box
                    component="img"
                    src={imagePreview || form.imageUrl}
                    alt="광고 이미지 미리보기"
                    sx={{
                      width: 200,
                      maxWidth: '100%',
                      aspectRatio: '1 / 1',
                      objectFit: 'cover',
                      borderRadius: 1,
                      border: `1px solid ${theme.palette.grey.line}`,
                    }}
                  />
                ) : null}
                <TextField
                  fullWidth
                  label="이미지 URL 직접 입력 (선택)"
                  helperText="파일 첨부 대신 이미 업로드된 HTTPS 이미지 주소를 사용할 때만 입력합니다."
                  {...field('imageUrl')}
                />
              </Stack>
            ) : null}

            {form.format ? (
              <Accordion
                disableGutters
                elevation={0}
                sx={{ border: `1px solid ${theme.palette.grey.line}` }}
              >
                <AccordionSummary>
                  <Typography fontWeight={600}>영어 문구 입력 (선택)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      label="앱에 표시할 영문 광고주명"
                      helperText="한국어 공개명이 있으면 비울 때 한국어명을 표시하고, 공개명이 모두 비면 ‘Ad’만 표시합니다."
                      {...field('sponsorNameEn')}
                    />
                    {form.format !== 'NATIVE_ALBUM' ? (
                      <>
                        <TextField
                          fullWidth
                          label={
                            form.format === 'COLLECTION' ? '영문 컬렉션 제목' : '영문 광고 제목'
                          }
                          inputProps={{ maxLength: 160 }}
                          {...field('headlineEn')}
                        />
                        <TextField
                          fullWidth
                          label={
                            form.format === 'COLLECTION' ? '영문 컬렉션 설명' : '영문 광고 설명'
                          }
                          inputProps={{ maxLength: 240 }}
                          {...field('subheadlineEn')}
                        />
                      </>
                    ) : null}
                    <TextField
                      fullWidth
                      label="영문 광고 표시 이유"
                      helperText="비워두면 한국어 문구를 표시합니다."
                      {...field('whyThisAdEn')}
                    />
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ) : null}

            {form.format === 'NATIVE_ALBUM' ? (
              <Alert severity="info">
                선택한 앨범의 커버·앨범명·아티스트명이 표시되고, 누르면 해당 앨범 상세로 이동합니다.
              </Alert>
            ) : null}
            {form.format === 'COLLECTION' ? (
              <TextField
                fullWidth
                label="컬렉션 제목 랜딩 URL (선택)"
                helperText="HTTPS 주소를 입력하면 앱 제목에 ›가 표시되고 제목을 눌러 외부 페이지로 이동합니다. 비우면 제목 링크가 꺼집니다. 각 앨범은 계속 앨범 상세로 이동합니다."
                inputProps={{ maxLength: 2048 }}
                {...field('collectionLandingUrl')}
              />
            ) : null}
            {form.format === 'COLLECTION' ? (
              <Alert severity="info">
                아래 순서대로 앨범 레일을 구성하며, 각 앨범을 누르면 해당 앨범 상세로 이동합니다.
              </Alert>
            ) : null}
            {form.format === 'INLINE_BANNER' ? (
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>이동 대상</InputLabel>
                  <Select
                    label="이동 대상"
                    value={form.destinationType}
                    onChange={(event) => changeDestinationType(event.target.value)}
                  >
                    <MenuItem value="ALBUM">앨범</MenuItem>
                    <MenuItem value="ARTIST">아티스트</MenuItem>
                    <MenuItem value="PLAYLIST">플리</MenuItem>
                    <MenuItem value="EXTERNAL">외부 링크</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            ) : null}

            {form.format === 'INLINE_BANNER' && form.destinationType === 'ALBUM' ? (
              <Alert severity="info">아래에서 선택한 앨범의 상세 화면으로 이동합니다.</Alert>
            ) : null}

            {form.format === 'INLINE_BANNER' &&
            ['ARTIST', 'PLAYLIST'].includes(form.destinationType) ? (
              <Stack spacing={1}>
                {form.destinationValue && !selectedTarget ? (
                  <Alert severity="info">
                    현재 연결된 대상은 그대로 유지됩니다. 다른 대상을 검색해 선택하면 교체됩니다.
                  </Alert>
                ) : null}
                <Autocomplete
                  options={targetOptions}
                  value={selectedTarget}
                  inputValue={targetSearchText}
                  loading={targetSearching || targetResolving}
                  filterOptions={(options) => options}
                  getOptionLabel={(option) => option.name || option.title || ''}
                  isOptionEqualToValue={(option, value) =>
                    form.destinationType === 'ARTIST'
                      ? artistIdentityOf(option) === artistIdentityOf(value)
                      : option.slug === value.slug
                  }
                  onInputChange={(_, value, reason) => {
                    if (reason !== 'reset') setTargetSearchText(value);
                  }}
                  onChange={selectTarget}
                  noOptionsText="검색 결과가 없습니다."
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      {...props}
                      key={`${form.destinationType}-${
                        form.destinationType === 'ARTIST' ? artistIdentityOf(option) : option.slug
                      }`}
                    >
                      <Avatar
                        src={option.artistImageUrl || option.imageUrl}
                        alt=""
                        sx={{ width: 36, height: 36, mr: 1.5 }}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography noWrap>{option.name || option.title}</Typography>
                        {option.username ? (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {option.username}
                          </Typography>
                        ) : null}
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={
                        form.destinationType === 'ARTIST'
                          ? '이동할 아티스트 검색'
                          : '이동할 플리 검색'
                      }
                      helperText={
                        form.destinationType === 'ARTIST'
                          ? '이름을 한 글자 이상 입력하세요. 아직 등록되지 않은 아티스트는 선택할 때 등록됩니다.'
                          : '이름을 두 글자 이상 입력하고 검색 결과에서 선택해주세요.'
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {targetSearching || targetResolving ? (
                              <CircularProgress size={18} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Stack>
            ) : null}

            {form.format === 'INLINE_BANNER' && form.destinationType === 'EXTERNAL' ? (
              <TextField
                required
                fullWidth
                label="클릭하면 열 웹 주소"
                placeholder="https://example.com"
                helperText="https://로 시작하는 전체 주소를 입력해주세요."
                {...field('destinationValue')}
              />
            ) : null}

            {form.format &&
            (form.format !== 'INLINE_BANNER' || form.destinationType === 'ALBUM') ? (
              <Stack spacing={1}>
                <Typography fontWeight={600}>
                  {hasCollectionPlacement ? '컬렉션에 표시할 앨범 (2개 이상)' : '표시할 앨범 (1개)'}
                </Typography>
                {hasCollectionPlacement ? (
                  <Alert severity="info">
                    앨범을 검색해 여러 개 선택하세요. 아래 번호 순서가 앱의 가로 레일 순서이며, 같은
                    앨범에는 광고 마크를 반복하지 않고 컬렉션 제목 아래에 한 번만 표시합니다.
                  </Alert>
                ) : null}
                <Autocomplete
                  multiple
                  filterSelectedOptions
                  options={albumOptions}
                  value={form.selectedAlbums}
                  inputValue={albumSearchText}
                  loading={albumSearching || albumResolving}
                  filterOptions={(options) => options}
                  getOptionLabel={(option) =>
                    `${option.artist?.name || '아티스트 미상'} · ${option.title || ''}${
                      option.contentRating === 'EXPLICIT' ? ' · Explicit' : ''
                    }`
                  }
                  isOptionEqualToValue={(option, value) =>
                    albumIdentityOf(option) === albumIdentityOf(value)
                  }
                  onInputChange={(_, value, reason) => {
                    if (reason !== 'reset') setAlbumSearchText(value);
                  }}
                  onChange={selectAlbums}
                  noOptionsText={
                    albumSearchText.trim().length < 1
                      ? '한 글자 이상 입력해주세요.'
                      : 'Apple Music 카탈로그에서 앨범을 찾지 못했습니다.'
                  }
                  renderTags={() => null}
                  renderOption={(props, album) => (
                    <Box component="li" {...props} key={albumIdentityOf(album)}>
                      <Avatar
                        variant="rounded"
                        src={album.albumImageUrl}
                        alt=""
                        sx={{ width: 44, height: 44, mr: 1.5 }}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <Typography noWrap>{album.title}</Typography>
                          {album.contentRating === 'EXPLICIT' ? (
                            <Chip label="E" size="small" sx={{ height: 18 }} />
                          ) : null}
                        </Stack>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {[album.artist?.name || '아티스트 미상', album.releaseDate]
                            .filter(Boolean)
                            .join(' · ')}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="앨범 또는 아티스트 검색"
                      helperText="앱 검색과 같은 카탈로그를 조회합니다. 아직 등록되지 않은 앨범은 선택할 때 등록되며, Explicit 버전은 E로 구분됩니다."
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {albumSearching || albumResolving ? (
                              <CircularProgress size={18} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                {form.selectedAlbums.length ? (
                  <Stack spacing={1}>
                    {form.selectedAlbums.map((album, index) => (
                      <Paper
                        key={albumIdOf(album)}
                        variant="outlined"
                        sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Typography sx={{ width: 24, textAlign: 'center' }}>{index + 1}</Typography>
                        <Avatar variant="rounded" src={album.albumImageUrl} alt="" />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <Typography noWrap>{album.title}</Typography>
                            {album.contentRating === 'EXPLICIT' ? (
                              <Chip label="E" size="small" sx={{ height: 18 }} />
                            ) : null}
                          </Stack>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {[album.artist?.name || '아티스트 미상', album.releaseDate]
                              .filter(Boolean)
                              .join(' · ')}
                          </Typography>
                        </Box>
                        {hasCollectionPlacement ? (
                          <>
                            <Button
                              size="small"
                              disabled={index === 0}
                              onClick={() => moveSelectedAlbum(index, -1)}
                            >
                              위
                            </Button>
                            <Button
                              size="small"
                              disabled={index === form.selectedAlbums.length - 1}
                              onClick={() => moveSelectedAlbum(index, 1)}
                            >
                              아래
                            </Button>
                          </>
                        ) : null}
                        <Button
                          color="error"
                          size="small"
                          onClick={() => removeSelectedAlbum(index)}
                        >
                          제거
                        </Button>
                      </Paper>
                    ))}
                  </Stack>
                ) : null}
              </Stack>
            ) : null}

            <TextField
              required
              fullWidth
              label="광고 표시 이유"
              helperText="회원이 광고 표기를 눌러 ‘이 광고를 보는 이유’를 확인할 때 표시됩니다. 기본 문구를 그대로 사용해도 됩니다."
              {...field('whyThisAd')}
            />

            <Typography variant="h6" fontWeight={700} sx={{ pt: 1 }}>
              4. 노출 위치
            </Typography>
            <FormControl required fullWidth>
              <InputLabel>노출 위치</InputLabel>
              <Select
                multiple
                disabled={!form.format}
                label="노출 위치"
                value={form.placementKeys}
                onChange={(event) => changePlacements(event.target.value)}
                renderValue={(selected) =>
                  orderedPlacementKeys(selected)
                    .map((key) => placementNames.get(key))
                    .join(', ')
                }
              >
                {selectablePlacements.map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    <Checkbox checked={form.placementKeys.includes(value)} />
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="h6" fontWeight={700} sx={{ pt: 1 }}>
              5. 위치별 노출 방식
            </Typography>
            {!form.placementKeys.length ? (
              <Alert severity="info">
                노출 위치를 선택하면 해당 위치에서 사용하는 설정이 표시됩니다.
              </Alert>
            ) : null}
            {form.placementKeys.length ? (
              <Accordion
                disableGutters
                elevation={0}
                sx={{ border: `1px solid ${theme.palette.grey.line}` }}
              >
                <AccordionSummary>
                  <Typography fontWeight={600}>위치별 노출 방식 확인 및 설정</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    <Alert severity="info">
                      네이티브 앨범은 앱에서 등장할 앨범 순서를 직접 정합니다. 2열 앨범 목록의
                      인라인 카드는 앨범 2개를 한 행으로 세어 완성된 행 사이에만 배치합니다. 그 밖의
                      인라인 목록은 앞에 둘 일반 콘텐츠 수를 정합니다. 모든 계산은 광고를 제외한
                      일반 콘텐츠 기준이며, 앞서 노출된 광고가 다음 캠페인의 위치를 밀어내거나
                      차감하지 않습니다. 탐색 최신 앨범과 국내·해외 구분이 같은 앨범 탭 최신 발매는
                      선택과 순서가 자동으로 연결됩니다. 앨범 탭의 전체 필터에서는 국내·해외
                      캠페인을 각각 설정한 순서에 함께 표시합니다. 같은 캠페인을 국내와 해외 양쪽에
                      둔 경우에는 광고 앨범의 국내·해외 구분에 맞는 순서로 한 번만 표시합니다.
                    </Alert>
                    {orderedPlacementKeys(form.placementKeys).map((placementKey) => {
                      const label = placementNames.get(placementKey) || placementKey;
                      const rule = PLACEMENT_RULES[placementKey];
                      const settings = canonicalPlacementSettings(
                        placementKey,
                        form.placementSettings[placementKey],
                        form.format,
                      );
                      const supportsRepeat = Boolean(
                        rule?.supportsRepeat && settings.format === 'INLINE_BANNER',
                      );
                      const startField = startFieldPresentation(
                        placementKey,
                        settings.format,
                        settings.startAfterOrganic,
                      );
                      const repeatField = repeatFieldPresentation(
                        placementKey,
                        settings.repeatEvery,
                      );
                      const placementConflicts = activePlacementConflicts.filter(
                        (conflict) => conflict.placementKey === placementKey,
                      );
                      return (
                        <Paper key={placementKey} variant="outlined" sx={{ p: 2 }}>
                          <Typography fontWeight={600} sx={{ mb: 1.5 }}>
                            {label}
                          </Typography>
                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                            {rule?.supportsStart && !rule.fixedSection ? (
                              <TextField
                                fullWidth
                                type="number"
                                inputProps={{ min: startField.min, max: startField.max }}
                                label={startField.label}
                                helperText={startField.helperText}
                                value={placementInputValue(
                                  placementKey,
                                  'startAfterOrganic',
                                  startField.value,
                                )}
                                onChange={(event) =>
                                  changePlacementNumberInput(
                                    placementKey,
                                    'startAfterOrganic',
                                    event.target.value,
                                    startField.toStoredValue,
                                  )
                                }
                                onBlur={() =>
                                  finishPlacementNumberInput(placementKey, 'startAfterOrganic')
                                }
                              />
                            ) : null}
                            {supportsRepeat ? (
                              <TextField
                                fullWidth
                                type="number"
                                inputProps={{ min: repeatField.min }}
                                label={repeatField.label}
                                helperText={repeatField.helperText}
                                value={placementInputValue(
                                  placementKey,
                                  'repeatEvery',
                                  repeatField.value,
                                )}
                                onChange={(event) =>
                                  changePlacementNumberInput(
                                    placementKey,
                                    'repeatEvery',
                                    event.target.value,
                                    repeatField.toStoredValue,
                                    true,
                                  )
                                }
                                onBlur={() =>
                                  finishPlacementNumberInput(placementKey, 'repeatEvery')
                                }
                              />
                            ) : null}
                            {supportsRepeat ? (
                              <TextField
                                fullWidth
                                type="number"
                                disabled={settings.repeatEvery === ''}
                                inputProps={{ min: 1, max: 3 }}
                                label="목록 내 최대 노출 횟수 (1~3)"
                                helperText="현재 불러온 목록에서 같은 캠페인의 최대 횟수"
                                value={placementInputValue(
                                  placementKey,
                                  'maxPerScreen',
                                  settings.maxPerScreen,
                                )}
                                onChange={(event) =>
                                  changePlacementNumberInput(
                                    placementKey,
                                    'maxPerScreen',
                                    event.target.value,
                                    (value) => value,
                                  )
                                }
                                onBlur={() =>
                                  finishPlacementNumberInput(placementKey, 'maxPerScreen')
                                }
                              />
                            ) : null}
                          </Stack>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 1, display: 'block' }}
                          >
                            {placementKey === 'SEARCH_RESULTS'
                              ? '통합 검색 화면에서는 보여줄 결과가 설정 수보다 적으면 마지막 미리보기 결과 뒤에 표시합니다. 카테고리별 전체 결과에서는 설정한 행 수 뒤에 표시합니다.'
                              : rule?.fixedSection
                                ? '이 위치는 정해진 섹션에 한 번만 표시되어 순서·반복 설정이 없습니다.'
                                : settings.format === 'NATIVE_ALBUM'
                                  ? '네이티브 앨범은 선택한 위치에 한 번만 표시합니다.'
                                  : '인라인 카드는 첫 위치와 반복 횟수를 설정할 수 있습니다.'}
                          </Typography>
                          {placementConflicts.length ? (
                            <Alert severity="error" sx={{ mt: 1.5 }}>
                              {placementConflicts.map(({ campaign, slots }) => (
                                <Typography
                                  key={`${campaign.id}-${slots.join('-')}`}
                                  variant="body2"
                                >
                                  ‘{campaign.internalName}’ 캠페인과{' '}
                                  {slots
                                    .map((slot) =>
                                      placementSlotDescription(placementKey, settings.format, slot),
                                    )
                                    .join(', ')}{' '}
                                  위치가 겹칩니다. 두 캠페인 중 하나의 등장 순서·반복 간격·기간을
                                  조정해주세요.
                                </Typography>
                              ))}
                            </Alert>
                          ) : null}
                        </Paper>
                      );
                    })}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ) : null}

            <Typography variant="h6" fontWeight={700} sx={{ pt: 1 }}>
              6. 기간과 상태
            </Typography>
            <Box>
              <Typography variant="caption" color="text.secondary">
                기간을 비우면 즉시 시작해 계속 노출됩니다. 입력 예시: 2030. 01. 01. 18:30
              </Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 0.75 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
                  <MaskedDateTimeField label="시작" {...dateTimeField('startsAt')} />
                  <Button
                    type="button"
                    variant="outlined"
                    disabled={!form.startsAt}
                    aria-label="시작 날짜와 시간 지우기"
                    onClick={() => clearField('startsAt')}
                    sx={{ minWidth: 72, whiteSpace: 'nowrap' }}
                  >
                    지우기
                  </Button>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
                  <MaskedDateTimeField label="종료" {...dateTimeField('endsAt')} />
                  <Button
                    type="button"
                    variant="outlined"
                    disabled={!form.endsAt}
                    aria-label="종료 날짜와 시간 지우기"
                    onClick={() => clearField('endsAt')}
                    sx={{ minWidth: 72, whiteSpace: 'nowrap' }}
                  >
                    지우기
                  </Button>
                </Stack>
              </Stack>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.isActive}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, isActive: event.target.checked }))
                  }
                />
              }
              label="설정한 기간에 캠페인 활성화"
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: '-12px !important' }}>
              체크하지 않으면 중지 상태의 초안으로 저장됩니다.
            </Typography>
            {activePlacementConflicts.length ? (
              <Alert severity="warning">
                같은 기간·위치에서 동일한 형식의 광고 슬롯이 겹칩니다. 위 ‘위치별 노출 방식’의 빨간
                안내에서 충돌 캠페인과 일반 콘텐츠 기준 위치를 확인해주세요. 슬롯이 다르면 같은
                형식의 캠페인도 함께 활성화할 수 있고, 다른 광고 형식도 같은 기간에 사용할 수
                있습니다.
              </Alert>
            ) : null}

            <Typography variant="h6" fontWeight={700} sx={{ pt: 1 }}>
              7. 저장 전 확인
            </Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={0.75}>
                <Typography>
                  <strong>형식:</strong> {FORMAT_LABELS.get(form.format) || '선택하지 않음'}
                </Typography>
                <Typography>
                  <strong>소재:</strong>{' '}
                  {form.format === 'NATIVE_ALBUM'
                    ? form.selectedAlbums[0]?.title || '앨범을 선택하지 않음'
                    : form.format === 'COLLECTION'
                      ? `${form.headline || '제목 없음'} · 앨범 ${form.selectedAlbums.length}개`
                      : form.headline || '제목 없음'}
                </Typography>
                <Typography>
                  <strong>노출 위치:</strong>{' '}
                  {form.placementKeys.length
                    ? orderedPlacementKeys(form.placementKeys)
                        .map((key) => placementNames.get(key))
                        .join(', ')
                    : '선택하지 않음'}
                </Typography>
                <Typography>
                  <strong>기간:</strong> {form.startsAt || '즉시'} ~ {form.endsAt || '계속'}
                </Typography>
                <Typography>
                  <strong>저장 상태:</strong> {form.isActive ? '활성화' : '초안/중지'}
                </Typography>
              </Stack>
            </Paper>
            {form.format ? (
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                <Typography fontWeight={600} sx={{ mb: 1.5 }}>
                  앱 표시 미리보기
                </Typography>
                {form.format === 'NATIVE_ALBUM' ? (
                  <Box sx={{ width: 180 }}>
                    {form.selectedAlbums[0]?.albumImageUrl ? (
                      <Box
                        component="img"
                        src={form.selectedAlbums[0].albumImageUrl}
                        alt=""
                        sx={{ width: 180, height: 180, objectFit: 'cover', borderRadius: 1 }}
                      />
                    ) : (
                      <Box
                        sx={{ width: 180, height: 180, borderRadius: 1, bgcolor: 'action.hover' }}
                      />
                    )}
                    <Typography fontWeight={600} noWrap sx={{ mt: 1 }}>
                      {form.selectedAlbums[0]?.title || '앨범을 선택해주세요'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {form.selectedAlbums[0]?.artist?.name || '아티스트'}
                    </Typography>
                    <Typography variant="caption" color="primary">
                      {form.sponsorName ? `광고 · ${form.sponsorName}` : '광고'}
                    </Typography>
                  </Box>
                ) : null}
                {form.format === 'COLLECTION' ? (
                  <Stack spacing={1}>
                    <Typography fontWeight={700}>
                      {form.headline || '컬렉션 제목'}
                      {form.collectionLandingUrl.trim() ? ' ›' : ''}
                    </Typography>
                    {form.subheadline ? (
                      <Typography variant="body2" color="text.secondary">
                        {form.subheadline}
                      </Typography>
                    ) : null}
                    <Typography variant="caption" color="primary">
                      {form.sponsorName ? `광고 · ${form.sponsorName}` : '광고'}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ overflowX: 'auto' }}>
                      {form.selectedAlbums.length ? (
                        form.selectedAlbums.map((album) => (
                          <Box key={albumIdOf(album)} sx={{ width: 92, flex: '0 0 auto' }}>
                            <Avatar
                              variant="rounded"
                              src={album.albumImageUrl}
                              alt=""
                              sx={{ width: 92, height: 92 }}
                            />
                            <Typography variant="caption" noWrap display="block">
                              {album.title}
                            </Typography>
                          </Box>
                        ))
                      ) : (
                        <Typography color="text.secondary">
                          앨범을 두 개 이상 선택해주세요.
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                ) : null}
                {form.format === 'INLINE_BANNER' ? (
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{ position: 'relative', minHeight: 88 }}
                  >
                    {imagePreview || form.imageUrl || form.selectedAlbums[0]?.albumImageUrl ? (
                      <Avatar
                        variant="rounded"
                        src={imagePreview || form.imageUrl || form.selectedAlbums[0]?.albumImageUrl}
                        alt=""
                        sx={{ width: 88, height: 88 }}
                      />
                    ) : null}
                    <SponsoredInlinePreview
                      headline={form.headline}
                      subheadline={form.subheadline}
                    />
                    <Typography
                      sx={{
                        position: 'absolute',
                        right: 8,
                        bottom: 4,
                        fontSize: 9,
                        lineHeight: '12px',
                        color: 'text.secondary',
                      }}
                    >
                      광고
                    </Typography>
                  </Stack>
                ) : null}
              </Paper>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={saving}>
            취소
          </Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? '저장 중…' : '저장'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deletingCampaign)} onClose={closeDelete} maxWidth="xs" fullWidth>
        <DialogTitle>캠페인 삭제</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {deleteError ? <Alert severity="error">{deleteError}</Alert> : null}
            <Typography>
              <strong>{deletingCampaign?.internalName}</strong> 캠페인을 삭제할까요?
            </Typography>
            <Alert severity="warning">
              삭제하면 캠페인 소재, 노출 위치, 전달·클릭 통계와 신고 기록이 함께 영구 삭제됩니다.
              활성 캠페인은 앱 노출도 즉시 중단되며 되돌릴 수 없습니다.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDelete} disabled={deleting}>
            취소
          </Button>
          <Button color="error" variant="contained" onClick={confirmDelete} disabled={deleting}>
            {deleting ? '삭제 중…' : '삭제'}
          </Button>
        </DialogActions>
      </Dialog>
    </ProgressBar>
  );
}
